import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import { ShieldCheck, ShieldAlert, PhoneOff, Copy, FileWarning } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);

function IssueCard({ label, count, pct, icon: Icon, testId, tone = "warn" }) {
  const tones = {
    warn: "text-amber-700 bg-amber-100/70",
    danger: "text-rose-700 bg-rose-100/70",
    info: "text-sky-700 bg-sky-100/70",
  };
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{fmt(count)}</div>
          <div className="text-xs text-slate-500 mt-1">{fmtPct(pct)} of rows</div>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone === "danger" ? "bg-rose-500" : tone === "info" ? "bg-sky-500" : "bg-amber-500"}`}
             style={{ width: `${Math.min(100, Number(pct) || 0)}%` }} />
      </div>
    </div>
  );
}

export default function DataQualityPage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/data-quality", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  // Upload-gap detector — flags (brand × campaign) days that look
  // suspiciously missing. Independent of the main data-quality window
  // filter because it always inspects the trailing 14 calendar days.
  useEffect(() => {
    setGapsLoading(true);
    const p = {};
    if (params.brand_id) p.brand_id = params.brand_id;
    if (params.team_id) p.team_id = params.team_id;
    if (params.campaign_id) p.campaign_id = params.campaign_id;
    api.get("/ops/reports/upload-gaps", { params: p })
      .then((r) => setGaps(r.data))
      .catch(() => setGaps(null))
      .finally(() => setGapsLoading(false));
  }, [params.brand_id, params.team_id, params.campaign_id]);

  const totals = data?.totals || {};
  const health = data?.health_score ?? 100;
  const healthColor = health >= 90 ? "#22C55E" : health >= 70 ? "#F59E0B" : "#EF4444";

  const issueChart = useMemo(() => ([
    { name: "Missing disp",   value: totals.missing_disposition_pct || 0, fill: "#F59E0B" },
    { name: "Missing agent",  value: totals.missing_agent_pct || 0,       fill: "#8B5CF6" },
    { name: "Invalid phone",  value: totals.invalid_phone_pct || 0,       fill: "#EF4444" },
    { name: "Duplicate rows", value: totals.duplicate_rows_pct || 0,      fill: "#0EA5E9" },
  ]), [totals]);

  const { rows: uploads, thProps } = useSortableTable(data?.per_upload || [], {
    initialKey: "total_issues", initialDir: "desc",
    accessors: { upload_name: (r) => (r.upload_name || "").toLowerCase() },
  });

  const healthGauge = [{ name: "health", value: health, fill: healthColor }];

  return (
    <div className="space-y-6" data-testid="dq-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Data quality
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Missing dispositions, invalid phone numbers, unassigned agents and duplicate rows.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="dq" />
      </div>

      <LoadingOverlay loading={loading} testId="dq-loading">
        {gaps && gaps.gap_count > 0 && (
          <div className="glass-card p-4 border-l-4 border-rose-500 bg-rose-50/40 mb-4" data-testid="dq-upload-gaps">
            <div className="flex items-start gap-2">
              <FileWarning className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-semibold text-rose-900">
                  {gaps.gap_count} missing upload{gaps.gap_count === 1 ? "" : "s"} detected in the last {gaps.lookback_days} days
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  These (brand × campaign) combinations had no rows on the flagged
                  dates despite neighbouring days being active. Users called on those
                  days will not receive credit until the missing files are uploaded.
                </div>
                <div className="mt-3 space-y-1 max-h-56 overflow-auto">
                  {gaps.gaps.map((g) => (
                    <div key={`${g.brand_id}-${g.campaign_id}`}
                         className="flex items-start justify-between gap-3 text-xs border-b border-rose-100 pb-1"
                         data-testid={`gap-${g.brand_id}-${g.campaign_id}`}>
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-slate-900">
                          Brand {g.brand_id} · {g.campaign_name}
                        </span>
                        <span className="text-slate-500">
                          {" · "}usually {g.avg_rows_on_active_days.toLocaleString()} rows/day
                        </span>
                      </div>
                      <div className="text-rose-800 font-mono text-right shrink-0">
                        {g.missing_dates.join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Fix: ask the dialler team for the missing file(s), upload via Ops
                  Uploads, then rebuild the rollup ({" "}
                  <span className="font-mono">
                    POST /api/admin/rollup-safety-rebuild
                  </span>{" "}
                  or wait for the nightly 03:00 IST job).
                </div>
              </div>
            </div>
          </div>
        )}
        {gaps && gaps.gap_count === 0 && !gapsLoading && (
          <div className="glass-card p-3 border-l-4 border-emerald-500 bg-emerald-50/40 mb-4 text-sm text-emerald-900 flex items-center gap-2" data-testid="dq-upload-gaps-clear">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            No upload gaps detected in the last {gaps.lookback_days} days.
          </div>
        )}
        {/* Health gauge + KPIs */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="glass-card p-5 flex items-center gap-4" data-testid="dq-health">
            <div className="w-32 h-32 relative">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="70%" outerRadius="100%" data={healthGauge} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-outfit font-semibold tabular-nums" style={{ color: healthColor }}>{fmt(health)}</div>
                  <div className="text-[10px] uppercase text-slate-500">Health</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Health score</div>
              <div className="text-sm text-slate-600 mt-1">
                {health >= 90 ? "Excellent — dataset is clean." :
                 health >= 70 ? "Some quality issues — inspect the largest contributors." :
                 "Significant hygiene issues, please investigate."}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Across <span className="font-semibold text-slate-800">{fmt(totals.total_rows)}</span> rows in the selected window.
              </div>
            </div>
          </div>

          <IssueCard label="Missing disposition"
                     count={totals.missing_disposition}
                     pct={totals.missing_disposition_pct}
                     icon={FileWarning} tone="warn"
                     testId="dq-missing-disp" />
          <IssueCard label="Invalid phone"
                     count={totals.invalid_phone}
                     pct={totals.invalid_phone_pct}
                     icon={PhoneOff} tone="danger"
                     testId="dq-invalid-phone" />
          <IssueCard label="Duplicate rows"
                     count={totals.duplicate_rows}
                     pct={totals.duplicate_rows_pct}
                     icon={Copy} tone="info"
                     testId="dq-duplicates" />
        </div>

        {/* Issue-rate bar chart */}
        <div className="glass-card p-5 mt-8" data-testid="dq-chart">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Issue rate breakdown (% of rows)
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={issueChart} margin={{ top: 16, right: 30, left: 0, bottom: 8 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`, "Rate"]} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {issueChart.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Per-upload table */}
        <div className="glass-card p-5 mt-8" data-testid="dq-per-upload">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Worst uploads (top 20 by issue count)
          </div>
          <div className="overflow-auto max-h-[55vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...thProps("upload_name", "left")}>Upload</SortableTh>
                  <SortableTh {...thProps("rows")}>Rows</SortableTh>
                  <SortableTh {...thProps("missing_disposition")}>No disp</SortableTh>
                  <SortableTh {...thProps("missing_agent")}>No agent</SortableTh>
                  <SortableTh {...thProps("invalid_phone")}>Invalid phone</SortableTh>
                  <SortableTh {...thProps("total_issues")}>Total issues</SortableTh>
                  <SortableTh {...thProps("issues_pct")}>Rate</SortableTh>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.upload_id || u.upload_name} className="border-b border-slate-50/70">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-800 truncate max-w-[280px]">{u.upload_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{u.upload_id}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(u.rows)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(u.missing_disposition)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(u.missing_agent)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(u.invalid_phone)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-slate-900">{fmt(u.total_issues)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded tabular-nums ${u.issues_pct >= 25 ? "bg-red-50 text-red-700" : u.issues_pct >= 10 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {fmtPct(u.issues_pct)}
                      </span>
                    </td>
                  </tr>
                ))}
                {uploads.length === 0 && (
                  <tr><td colSpan={7} className="py-4 text-center text-slate-500">
                    <ShieldCheck className="w-5 h-5 inline mr-1 text-emerald-500" /> No quality issues detected in this window.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
